import styled from 'styled-components';

import OngoingLottie from '@/assets/lottie/loading-dot-ongoing.json';
import WaitLottie from '@/assets/lottie/loading-dot-wait.json';
import CustomLottieIcon from '@/components/atom/CustomLottieIcon';
import DefaultDoctorProfile from '@/components/atom/icon/DefaultDoctorProfile';
import InfiniteScroll from '@/components/common/InfiniteScroll';
import LazyImage from '@/components/molecules/LazyImage';
import QuestionItem from '@/components/molecules/QuestionList/QuestionItem';
import ScrollTopStickyButton from '@/components/molecules/ScrollTopStickyButton';
import { SearchQuestionViewModel } from '@/hooks/search/qna/useSearchQuestionViewModel';
import theme from '@/styles/theme';
import { QuestionStatus } from '@/types/bff/gql-schema';

import { EQuestionAnsweredStatus } from '@/types/search';
import EmptySearchResult from '../EmptySearchResult';

interface Props {
  viewModel: SearchQuestionViewModel;
}

const QnaSearchResult = ({ viewModel }: Props) => {
  if (!viewModel.searchedQnaData?.pages?.length) {
    return <EmptySearchResult handleClickRequestButton={viewModel.handleClickRequestButton} />;
  }

  return (
    <>
      <InfiniteScroll
        rootMargin={'0px 0px 0px 100px'}
        isLoading={viewModel.isSearchedQnaLoading}
        hasNextPage={viewModel.hasSearchedQnaNextPage}
        fetchNextPage={viewModel.fetchSearchedQnaNextPage}
      >
        {viewModel.searchedQnaData?.pages.map((question) => {
          return (
            <QuestionItem
              isPaidQuestion={false}
              key={question.question.questionPid}
              questionCard={question}
              onClickQuestion={viewModel.onClickQuestion}
            >
              <QuestionItem.BottomLeft>
                {question.answeredStatus === EQuestionAnsweredStatus.Answered ? (
                  <QuestionBottomAnswerStatus>
                    {/* 답변한 의사 프로필/카운트 (최대 5개) */}
                    <QuestionDoctorImageWrapper>
                      {question.answererProfileImageUrls.slice(0, 5).map((image, key) => (
                        <QuestionDoctorImage imageKey={key} key={`answered-doctor-${key}`}>
                          <LazyImage
                            width={28}
                            height={28}
                            objectFit={'cover'}
                            loader={<DefaultDoctorProfile />}
                            src={image}
                            alt={'답변한 의사 프로필 이미지'}
                          />
                        </QuestionDoctorImage>
                      ))}
                    </QuestionDoctorImageWrapper>
                    {question.answererCount}개의 답변
                  </QuestionBottomAnswerStatus>
                ) : question.question.status !== QuestionStatus.AwaitingBasicAnswer ? (
                  // 답변 중 / 답변 확인 중
                  // 목록
                  <QuestionBottomAnswerStatus
                    fontColor={
                      question.answeredStatus === EQuestionAnsweredStatus.Answering
                        ? theme.color.primary500
                        : theme.color.blue500
                    }
                  >
                    <CustomLottieIcon
                      width={24}
                      height={24}
                      animationData={
                        question.answeredStatus === EQuestionAnsweredStatus.Answering ? OngoingLottie : WaitLottie
                      }
                      style={{ marginRight: 8 }}
                    />
                    {question.answeredStatusText}
                  </QuestionBottomAnswerStatus>
                ) : (
                  <></>
                )}
              </QuestionItem.BottomLeft>
            </QuestionItem>
          );
        })}
      </InfiniteScroll>
      <ScrollTopStickyButton />
    </>
  );
};

export default QnaSearchResult;

const QuestionBottomAnswerStatus = styled.div<{ fontColor?: string }>`
  display: flex;
  align-items: center;
  ${({ theme, fontColor }) => `
    ${theme.typography.UI14SB};
    color: ${fontColor ?? theme.color.gray700};
  `};
  & > div {
    margin-right: 8px;
  }
`;

const QuestionDoctorImageWrapper = styled.div`
  display: flex;
  position: relative;
  margin-right: 8px;
`;

const QuestionDoctorImage = styled.div<{ imageKey: number }>`
  img {
    border: 2px solid white;
  }
  ${({ imageKey }) => `
    margin-left: -${imageKey ? 8 : 0}px;
    z-index: ${5 - imageKey};
  `};
`;
